import { useState } from "react";
import { FormControlLabel, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, alpha } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { useHistory } from "@/compat/router";
import CardBase from "../../../components/cards/CardBase";
import ActionStatusChip from "../ActionStatusChip";
import useRiskAssessmentActions from "../../hooks/queries/useRiskAssessmentActions";
import useContactsForAppAndSite from "../../../hooks/useContactsForAppAndSite";
import { formatShortDate } from "../../../utils/dateTimeUtils";
import { moduleUrls } from "../../constants/moduleAreaConstants";
import { actionsStatus } from "../../constants/actionStatusConstants";
import { baseRoute } from "../../routes";

const useStyles = makeStyles((theme) => ({
    row: {
        cursor: "pointer",
        "&:hover": {
            backgroundColor: alpha(theme.palette.secondary.main, 0.08),
        },
    },
    actionCell: {
        maxWidth: 360,
        whiteSpace: "normal",
        wordWrap: "break-word",
    },
    empty: {
        padding: theme.spacing(3, 1),
        color: theme.palette.text.secondary,
        textAlign: "center",
    },
    toggle: {
        marginRight: 0,
    },
}));

// The whole action plan for the assessment, not one completion's slice of it.
export default function ActionPlanCard({ riskAssessmentId }) {
    const classes = useStyles();
    const history = useHistory();
    const [showCompleted, setShowCompleted] = useState(false);
    const { data, isLoading, isFetching, error } = useRiskAssessmentActions(riskAssessmentId);
    const contacts = useContactsForAppAndSite({ selectedSiteId: null });

    const allActions = Array.isArray(data) ? data : [];
    // Default to the current/active actions (completed hidden); most recent first.
    const visibleActions = [...allActions]
        .filter((a) => showCompleted || a.status !== actionsStatus.COMPLETED)
        .sort((a, b) => (b.id ?? 0) - (a.id ?? 0));

    const assigneeName = (externalId) => {
        const c = contacts?.data?.[externalId];
        return c ? `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || "Unassigned" : "Unassigned";
    };

    const goToAction = (action) => {
        let path = `${baseRoute}/${moduleUrls[action.moduleType]}/action/${action.id}`;
        if (action.isDri) path += "/true";
        history.push(path);
    };

    const emptyMessage =
        allActions.length > 0
            ? "No active actions. Turn on 'Show completed' to see completed actions."
            : "No actions have been raised for this risk assessment.";

    return (
        <CardBase
            title="Action Plan"
            isLoading={Boolean(riskAssessmentId) && isLoading}
            isFetching={Boolean(riskAssessmentId) && isFetching}
            error={error}
            rightComponent={
                <FormControlLabel
                    className={classes.toggle}
                    control={<Switch size="small" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} />}
                    label="Show completed"
                />
            }
        >
            {visibleActions.length === 0 ? (
                <Typography className={classes.empty} variant="body2">
                    {emptyMessage}
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ref</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Assigned</TableCell>
                                <TableCell>Due</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleActions.map((action) => (
                                <TableRow key={`${action.id}-${action.isDri}`} className={classes.row} onClick={() => goToAction(action)}>
                                    <TableCell>{action.id}</TableCell>
                                    <TableCell className={classes.actionCell}>{action.description}</TableCell>
                                    <TableCell>
                                        <ActionStatusChip status={action.status} statusName={action.statusName} />
                                    </TableCell>
                                    <TableCell>{assigneeName(action.assignedEmployeeExternalId)}</TableCell>
                                    <TableCell>{action.dueDate ? formatShortDate(action.dueDate) : "N/A"}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </CardBase>
    );
}
